import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { loadableReady } from '@loadable/component';
import { createInstance } from '@optimizely/react-sdk';
import createStore from 'store/create';
import initialize from 'utils/app/initialize';
import { setLoggerStore, logError } from 'utils/logger';
import App from 'containers/App';
import { TrackingClient } from '@sparefoot/tracking-client/dist/TrackingClient';
import { ReactSegmentWrapper } from '@sparefoot/segment-react-wrapper';

const decodedState = globalThis?.__PRELOADED_STATE__
	? JSON.parse(
			new TextDecoder().decode(
				Uint8Array.from(atob(globalThis.__PRELOADED_STATE__), (c) =>
					c.codePointAt(0),
				),
			),
		)
	: null;

const store = createStore({ rollbar: globalThis.Rollbar }, decodedState);
setLoggerStore(store);
initialize(store);

const { app, user } = store.getState();

// Get Optimizely context from server-side rendering
const datafileFromServer = globalThis.__OPTIMIZELY_DATAFILE__;
const userFromServer = globalThis.__OPTIMIZELY_USER__;

// Create Optimizely client from pre-fetched datafile
let optimizelyClient = null;
if (datafileFromServer) {
	try {
		optimizelyClient = createInstance({
			datafile: datafileFromServer,
		});
	} catch (err) {
		logError('[Optimizely] Client initialization error:', err);
	}
}
if (optimizelyClient && userFromServer?.forcedDecisions?.length > 0) {
	for (const { flagKey, variationKey } of userFromServer.forcedDecisions) {
		try {
			optimizelyClient.setForcedDecision({ flagKey }, { variationKey });
		} catch (err) {
			logError(
				`[Optimizely] Failed to set forced decision for ${flagKey}:`,
				err,
			);
		}
	}
}

const getCdnUrl = (url) => {
	if (!url) {
		return undefined;
	}
	return url.startsWith('http') ? url : `https://${url}`;
};

TrackingClient.initialize(
	new ReactSegmentWrapper({
		writeKey: app.segmentWriteKey,
		visitId: user.visitId,
		environment: app.environment,
		siteId: app.id,
		host: app.baseUrl.replace(/^https?:\/\//i, ''),
		cdnURL: getCdnUrl(app.eventsCdnUrl),
		debug:
			process.env.NODE_ENV === 'development' &&
			process.env.RENDER_ENV === 'client',
		integrations: {
			'Segment.io': {
				apiHost: app.eventsApiUrl,
				protocol: 'https',
			},
		},
	}),
);

// Initialize Optimizely user and wait for ready before hydrating
(async () => {
	if (optimizelyClient) {
		try {
			const userId = userFromServer?.id || user.visitId;
			optimizelyClient.setUser({
				id: userId,
				attributes: userFromServer?.attributes,
			});

			await optimizelyClient.onReady({ timeout: 400 });
		} catch (err) {
			logError('[Optimizely] SDK ready timeout, hydrating anyway:', err);
		}
	}

	loadableReady(() => {
		const container = document.getElementById('js-page-content');
		const root = hydrateRoot(
			container,
			<App
				store={store}
				optimizelyClient={optimizelyClient}
				optimizelyUser={userFromServer}
			/>,
			{
				onRecoverableError: (error) => {
					logError('[Hydration]', error);
				},
			},
		);

		if (module.hot) {
			module.hot.accept(['./containers/App', './pages/Bundles'], () => {
				const NextApp = require('./containers/App').default;
				root.render(
					<NextApp
						store={store}
						optimizelyClient={optimizelyClient}
						optimizelyUser={userFromServer}
					/>,
				);
			});
		}
	});

	// Clean up global variables after hydration
	delete globalThis.__OPTIMIZELY_DATAFILE__;
	delete globalThis.__OPTIMIZELY_USER__;
})();
